import { useQuery } from '@tanstack/react-query';
import { db } from '../../firebaseConfig';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { getUserPosts } from '../../pages/auth/utils/firebase/userOperations';

const stale = 5 * 60_000; // 5 minutes
const gc = 30 * 60_000; // 30 minutes

const fetchSolutions = async (userId) => {
  if (!userId) return [];
  const sols = [];
  const solsRef = collection(db, 'solutions');
  const q1 = query(solsRef, where('creatorId', '==', userId), orderBy('createdAt', 'desc'), limit(25));
  const snap1 = await getDocs(q1);
  snap1.forEach(d => sols.push({ id: d.id, ...d.data() }));
  if (sols.length === 0) {
    const q2 = query(solsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'), limit(25));
    const snap2 = await getDocs(q2);
    snap2.forEach(d => sols.push({ id: d.id, ...d.data() }));
  }
  return sols;
};

const fetchComments = async (userId) => {
  if (!userId) return [];
  const list = [];
  const commentsRef = collection(db, 'comments');
  const q = query(commentsRef, where('userId', '==', userId), orderBy('timestamp', 'desc'), limit(50));
  const snap = await getDocs(q);
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return list;
};

const fetchProjects = async (userId) => {
  if (!userId) return [];
  const list = [];
  const projectsRef = collection(db, 'projects');
  const q = query(projectsRef, where('creatorId', '==', userId), orderBy('createdAt', 'desc'), limit(25));
  const snap = await getDocs(q);
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return list;
};

export const useProfileSolutions = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['profile', 'solutions', userId],
    queryFn: () => fetchSolutions(userId),
    enabled: !!userId && enabled,
    staleTime: stale,
    gcTime: gc,
    keepPreviousData: false,
  });
};

export const useProfileComments = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['profile', 'comments', userId],
    queryFn: () => fetchComments(userId),
    enabled: !!userId && enabled,
    staleTime: stale,
    gcTime: gc,
    keepPreviousData: false,
  });
};

export const useProfileProjects = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['profile', 'projects', userId],
    queryFn: () => fetchProjects(userId),
    enabled: !!userId && enabled,
    staleTime: stale,
    gcTime: gc,
    keepPreviousData: false,
  });
};

export const useProfilePosts = (userId, enabled = true) => {
  return useQuery({
    queryKey: ['profile', 'posts', userId],
    queryFn: () => getUserPosts(userId),
    enabled: !!userId && enabled,
    staleTime: stale,
    gcTime: gc,
    keepPreviousData: false,
  });
};

export const prefetchProfileTab = (queryClient, tab, userId) => {
  if (!queryClient || !userId) return;
  const opts = { staleTime: stale, gcTime: gc };
  switch (tab) {
    case 'posts':
      queryClient.prefetchQuery({ queryKey: ['profile', 'posts', userId], queryFn: () => getUserPosts(userId), ...opts });
      queryClient.prefetchQuery({ queryKey: ['profile', 'solutions', userId], queryFn: () => fetchSolutions(userId), ...opts });
      queryClient.prefetchQuery({ queryKey: ['profile', 'comments', userId], queryFn: () => fetchComments(userId), ...opts });
      break;
    case 'solutions':
      queryClient.prefetchQuery({ queryKey: ['profile', 'comments', userId], queryFn: () => fetchComments(userId), ...opts });
      queryClient.prefetchQuery({ queryKey: ['profile', 'projects', userId], queryFn: () => fetchProjects(userId), ...opts });
      break;
    case 'comments':
      queryClient.prefetchQuery({ queryKey: ['profile', 'solutions', userId], queryFn: () => fetchSolutions(userId), ...opts });
      break;
    case 'projects':
      queryClient.prefetchQuery({ queryKey: ['profile', 'solutions', userId], queryFn: () => fetchSolutions(userId), ...opts });
      break;
    default:
      break;
  }
};
